import type { CSSProperties } from 'react'

const PALETTE = [
  ['#C26345', '#F2D9A8'],
  ['#3F6E4E', '#DCE8C6'],
  ['#B98427', '#F5E2B3'],
  ['#7A4F6E', '#E8D2DA'],
  ['#3D5366', '#CFD8DD'],
  ['#8E3A2E', '#F0CDB6'],
] as const

export type MeepleAvatarProps = {
  name: string
  size?: number
  hue?: number
  shape?: 'meeple' | 'circle'
  // Ghost variant for non-user "guest" players: gray fill, dashed
  // outline, reduced opacity. Reads as "person but not a real account."
  ghost?: boolean
  style?: CSSProperties
}

const GHOST_FILL = '#c9b997'
const GHOST_FG = '#5c4a39'
const GHOST_OPACITY = 0.65

export const MeepleAvatar = ({
  name,
  size = 32,
  hue = 0,
  shape = 'meeple',
  ghost = false,
  style,
}: MeepleAvatarProps) => {
  const idx = Math.abs((name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0) + hue) % PALETTE.length
  const [paletteBg, paletteFg] = PALETTE[idx]!
  const bg = ghost ? GHOST_FILL : paletteBg
  const fg = ghost ? GHOST_FG : paletteFg
  const initial = name[0]?.toUpperCase() ?? '?'
  const clipId = `mp-${name.replace(/\s/g, '')}-${size}-${hue}-${ghost ? 'g' : 'n'}`

  if (shape === 'circle') {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: bg,
          color: fg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          fontSize: size * 0.42,
          lineHeight: 1,
          flexShrink: 0,
          boxShadow: ghost
            ? 'none'
            : '0 1px 0 rgba(255,255,255,.2) inset, 0 -1px 0 rgba(0,0,0,.15) inset',
          border: ghost ? `1.5px dashed ${fg}` : 'none',
          opacity: ghost ? GHOST_OPACITY : 1,
          ...style,
        }}
      >
        {initial}
      </div>
    )
  }

  // Stroke width scales with the size; the dashed outline traces the
  // meeple silhouette via the same path used for the clip.
  const meeplePath =
    'M20 4 C24 4 26 7 26 10 C26 12 25 13 25 14 L31 14 C34 14 36 16 36 19 L36 22 C36 24 35 25 33 25 L29 25 L29 33 C29 35 27 37 25 37 L15 37 C13 37 11 35 11 33 L11 25 L7 25 C5 25 4 24 4 22 L4 19 C4 16 6 14 9 14 L15 14 C15 13 14 12 14 10 C14 7 16 4 20 4 Z'

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      style={{ flexShrink: 0, opacity: ghost ? GHOST_OPACITY : 1, ...style }}
    >
      <defs>
        <clipPath id={clipId}>
          <path d={meeplePath} />
        </clipPath>
      </defs>
      <rect x="0" y="0" width="40" height="40" fill={bg} clipPath={`url(#${clipId})`} />
      {ghost && (
        <path
          d={meeplePath}
          fill="none"
          stroke={fg}
          strokeWidth={1.5}
          strokeDasharray="2.5 1.8"
          strokeLinejoin="round"
        />
      )}
      <text
        x="20"
        y="27"
        textAnchor="middle"
        fill={fg}
        fontFamily="var(--font-display)"
        fontWeight="700"
        fontSize="14"
        clipPath={`url(#${clipId})`}
      >
        {initial}
      </text>
    </svg>
  )
}
